import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Sparkles,
  Crown,
  LogOut,
  MoreVertical,
  Globe,
  Trash2,
  FolderOpen,
} from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createProject = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { name: newName.trim() });
      setOpen(false);
      setNewName("");
      nav(`/editor/${data.id}`);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      toast.error(detail);
      if (e.response?.status === 402) nav("/upgrade");
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  const sub = user?.subscription || {};
  const trialBadge =
    sub.is_master
      ? "Master Developer"
      : sub.status === "active"
      ? `${(sub.tier || "starter").toUpperCase()} Plan`
      : sub.trial_active
      ? "7-Day Pro Trial"
      : "Trial Expired";

  return (
    <div className="min-h-screen bg-[#E5E7EB]">
      {/* Topbar */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#002FA7] flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <span className="font-display font-bold text-base tracking-tight" data-testid="app-title">
            CreativeHub Website Pro Builder
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 font-medium" data-testid="subscription-badge">
            {sub.is_master && <Crown className="w-3.5 h-3.5 text-amber-500" />}
            {trialBadge}
          </div>
          {!sub.is_master && (
            <button
              className="ch-upgrade rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"
              onClick={() => nav("/upgrade")}
              data-testid="upgrade-plan-button"
            >
              <Crown className="w-4 h-4" />
              Upgrade your plan
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-sm font-bold"
                data-testid="user-avatar-button"
              >
                {user?.name?.[0]?.toUpperCase() || "U"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled className="text-xs">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/upgrade")} data-testid="menu-billing">
                Billing & Plans
              </DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold tracking-tighter">Your projects</h1>
            <p className="text-sm text-gray-500 mt-1">
              {sub.is_master
                ? "Master access — unlimited projects, full Pro features."
                : sub.limits?.max_projects
                ? `${projects.length} of ${sub.limits.max_projects} active projects`
                : "Manage your CreativeHub designs"}
            </p>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#002FA7] hover:bg-blue-800 text-white"
                data-testid="new-project-button"
              >
                <Plus className="w-4 h-4 mr-1" /> New project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a new project</DialogTitle>
              </DialogHeader>
              <Input
                placeholder="My amazing website"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createProject()}
                data-testid="new-project-name-input"
                autoFocus
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  onClick={createProject}
                  disabled={creating || !newName.trim()}
                  data-testid="new-project-create-button"
                  className="bg-[#002FA7] hover:bg-blue-800 text-white"
                >
                  {creating ? "Creating…" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded-xl bg-white p-12 text-center" data-testid="empty-state">
            <FolderOpen className="w-10 h-10 text-gray-300 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-xl font-semibold tracking-tight">No projects yet</h3>
            <p className="text-sm text-gray-500 mt-1">Hit &quot;New project&quot; to start designing.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
            {projects.map((p) => (
              <div
                key={p.id}
                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:ring-2 hover:ring-[#002FA7]/40 transition-all cursor-pointer ch-fade"
                onClick={() => nav(`/editor/${p.id}`)}
                data-testid={`project-card-${p.id}`}
              >
                <div className="aspect-[5/3] bg-gradient-to-br from-gray-100 to-gray-200 relative">
                  <div className="absolute inset-3 bg-white rounded shadow-sm flex flex-col gap-1.5 p-3">
                    <div className="h-1.5 w-1/2 bg-gray-200 rounded" />
                    <div className="h-1.5 w-3/4 bg-gray-200 rounded" />
                    <div className="h-1.5 w-2/3 bg-gray-200 rounded" />
                    <div className="mt-2 h-12 bg-gray-100 rounded" />
                  </div>
                  {p.published && (
                    <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white px-2 py-1 rounded">
                      Live
                    </span>
                  )}
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-display font-semibold tracking-tight truncate">{p.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{p.pages?.length || 0} pages</div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="p-1.5 rounded hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`project-menu-${p.id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => nav(`/editor/${p.id}`)}>
                        <Globe className="w-4 h-4 mr-2" /> Open editor
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => deleteProject(p.id)}
                        className="text-red-600"
                        data-testid={`project-delete-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
